import React, { createContext, useContext, useEffect, useState } from 'react'
import produce from 'immer'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import useReasonPolicyRule from 'src/core/hooks/use-reason-policy-rule'

import { Segments } from 'src/components/SegmentControl'

import Billing from './components/Billing'
import Sharing from './components/Sharing'

interface Params {
  reason_id: string
}

export type SegmentKeys = 'billing' | 'sharing'

export type AssignmentSegments = Segments<SegmentKeys>

interface SegmentControlContextValues {
  current: SegmentKeys
  segments: AssignmentSegments
  setCurrent(_: SegmentKeys): void
  setSegments(_: AssignmentSegments): void
  setDisabled(_: SegmentKeys, v: boolean): void
}

const segments: AssignmentSegments = {
  sharing: {
    disabled: false,
    title: 'web.assignment.sharing.title',
    renderContent: () => <Sharing />
  },
  billing: {
    disabled: true,
    title: 'web.assignment.billing.title',
    renderContent: () => <Billing />
  }
}

const initialValues: SegmentControlContextValues = {
  current: 'sharing',
  segments,
  setCurrent(_: SegmentKeys) {},
  setSegments(_: AssignmentSegments) {},
  setDisabled(_: SegmentKeys, v: boolean) {}
}

export const SegmentControlContext =
  createContext<SegmentControlContextValues>(initialValues)

export function useSegmentControlContext(): SegmentControlContextValues {
  return useContext(SegmentControlContext)
}

function Assignment({
  match: {
    params: { reason_id }
  }
}: RouteComponentProps<Params>) {
  const [current, setCurrent] = useState<SegmentKeys>(initialValues.current)
  const [segments, setSegments] = useState<AssignmentSegments>()

  const policyRule = useReasonPolicyRule(reason_id)

  useEffect(() => {
    const nextSegments = { ...initialValues.segments }

    if (policyRule) {
      if (!policyRule.enable_standard_billing) {
        delete nextSegments.billing
      }

      if (!policyRule.enable_standard_sharing) {
        delete nextSegments.sharing

        if (!!nextSegments.billing) nextSegments.billing.disabled = false

        setCurrent('billing')
      }
    }

    setSegments({ ...nextSegments })
  }, [policyRule])

  function setDisabled(key: SegmentKeys, value: boolean) {
    setSegments(
      produce(segments, draft => {
        if (!!draft[key]) draft[key].disabled = value
      })
    )
  }

  return (
    <SegmentControlContext.Provider
      value={{
        current,
        segments,
        setCurrent,
        setSegments,
        setDisabled
      }}
    >
      {segments && segments[current].renderContent()}
    </SegmentControlContext.Provider>
  )
}

export default withRouter(Assignment)
